import { Spinner } from '@tempmee/react';
import { type CSSProperties as ReactCSSProperties } from 'react';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Center,
  Column,
  EmptyScreen,
  SortingArrow,
  type TSortValue,
} from '~/commonComponents';
import {
  IGetOnsiteInterviewListViewEntity,
  OnsiteInterviewSortableField,
  OnsiteInterviewStatus,
} from '~/domain/PermanentJobs';
import OnsiteInterviewEmptyScreenIcon from '~/images/onsite-interview-empty-screen-icon.svg';
import { firePermJobOverviewAddInterviewsClicked } from '~/posthogs/events';
import { AddMoreOnsiteInterviewButton } from '~/components/PermanentJobsV2/components';
import { ListItem } from './ListItem';

export const Styles = {
  Table: {
    borderSpacing: '0 12px',
    borderCollapse: 'separate',
  } as ReactCSSProperties,
  TableHeader: {
    DateCol: {
      textAlign: 'left',
      width: '25%',
      paddingLeft: 38,
    } as ReactCSSProperties,
    TimeCol: {
      textAlign: 'left',
      width: '25%',
    } as ReactCSSProperties,
    RateCol: {
      textAlign: 'left',
      width: '8%',
    } as ReactCSSProperties,
    CandidateCol: {
      textAlign: 'center',
      width: '20%',
    } as ReactCSSProperties,
    StatusCol: {
      paddingRight: 38,
      textAlign: 'left',
      width: '22%',
    } as ReactCSSProperties,
  },
};

interface ICandidateListTableProps {
  data: IGetOnsiteInterviewListViewEntity[];
  isLoading: boolean;
  isShowStatusSorting: boolean;
  jobPostingId: string;
  sorting: TSortValue;
  status: OnsiteInterviewStatus | 'all';
  onSortingChange?: (value: TSortValue | undefined) => void;
}

const EMPTY_STATUS_COPY = {
  all: 'active',
  [OnsiteInterviewStatus.ActionNeeded]: 'action needed',
  [OnsiteInterviewStatus.Cancelled]: 'cancelled',
  [OnsiteInterviewStatus.Completed]: 'completed',
  [OnsiteInterviewStatus.Open]: 'open',
  [OnsiteInterviewStatus.Scheduled]: 'scheduled',
};

export const OnsiteInterviewListTable = ({
  data,
  isLoading,
  isShowStatusSorting,
  jobPostingId,
  sorting,
  status,
  onSortingChange,
}: ICandidateListTableProps) => {
  const isEmptyData = data.length === 0;

  if (!isLoading && isEmptyData) {
    return (
      <EmptyScreen
        Icon={() => (
          <img
            alt="onsite-interview-empty-screen-icon"
            src={OnsiteInterviewEmptyScreenIcon}
            height={170}
            width={170}
          />
        )}
        title={`No ${EMPTY_STATUS_COPY[status]} onsite interviews`}
        description={
          <Column alignItems="center" gap={48} marginBottom={146}>
            <Typography as="span" color={colors.neutral[500]}>
              Looks like you don’t have any {EMPTY_STATUS_COPY[status]} onsite interviews. Add a few
              more to continue evaluating new candidates and keep the hiring process moving forward.
            </Typography>

            <AddMoreOnsiteInterviewButton
              jobPostingId={jobPostingId}
              onTrack={() => firePermJobOverviewAddInterviewsClicked({ postingId: jobPostingId })}
            />
          </Column>
        }
      />
    );
  }

  return (
    <>
      <table style={Styles.Table}>
        <thead>
          <tr>
            <th style={Styles.TableHeader.DateCol}>
              <SortingArrow
                name={OnsiteInterviewSortableField.JobDateTime}
                sorting={sorting}
                style={{ justifyContent: 'flex-start' }}
                toggleOnly
                variant="primary"
                onChange={onSortingChange}
              >
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Date
                </Typography>
              </SortingArrow>
            </th>
            <th style={Styles.TableHeader.TimeCol}>
              <Typography as="span" bold color={colors.primary[500]} size="body_large">
                Time
              </Typography>
            </th>
            <th style={Styles.TableHeader.RateCol}>
              <Typography as="span" bold color={colors.primary[500]} size="body_large">
                Rate
              </Typography>
            </th>
            <th style={Styles.TableHeader.CandidateCol}>
              <Typography as="span" bold color={colors.primary[500]} size="body_large">
                Candidates
              </Typography>
            </th>
            <th style={Styles.TableHeader.StatusCol}>
              {isShowStatusSorting ? (
                <SortingArrow
                  name={OnsiteInterviewSortableField.Status}
                  sorting={sorting}
                  style={{ justifyContent: 'flex-start' }}
                  variant="primary"
                  onChange={onSortingChange}
                >
                  <Typography as="span" bold color={colors.primary[500]} size="body_large">
                    Status
                  </Typography>
                </SortingArrow>
              ) : (
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Status
                </Typography>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {!isLoading &&
            data.map((it) => (
              <tr key={it.id}>
                <td colSpan={5}>
                  <ListItem data={it} jobPostingId={jobPostingId} />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {isLoading && (
        <Center marginBlock={32}>
          <Spinner centered variant="default" size="md" />
        </Center>
      )}
    </>
  );
};
